/**
 * @fileoverview Main entry point for the KeyReply Webchat Widget (Vue 3)
 *
 * This file bootstraps the Vue 3 webchat application, configuring:
 * - Core-js polyfills for broad browser compatibility
 * - Element Plus component library (selectively imported for bundle optimization)
 * - Global utility functions via globalProperties
 * - Speech recognition plugin for voice input
 * - Device detection via provide/inject pattern
 * - Global $keyreply API exposed on window for external integration
 * - Trusted Types for Content Security Policy compliance
 *
 * @version 3.0.0
 * @author KeyReply Engineering Team
 *
 * @see https://vuejs.org/guide/essentials/application.html
 */

// Note: core-js polyfills removed - Vite 8 targets modern browsers (Chrome 111+, Firefox 114+, Safari 16.4+)
// If legacy browser support is needed, use @vitejs/plugin-legacy
import "@/assets/tailwind.css";
import { createApp, App as VueApp } from "vue";
import { createPinia } from "pinia";
import * as filters from "@/filters";
import { setupDOMPurifyWithTrustedTypes } from "./helpers/trustedTypes";

// Element Plus imports (Vue 3 compatible replacement for Element UI)
import {
  ElCheckbox,
  ElDialog,
  ElDivider,
  ElDrawer,
  ElDropdown,
  ElDropdownMenu,
  ElDropdownItem,
  ElInput,
  ElButton,
  ElTooltip,
  ElForm,
  ElFormItem,
  ElTabs,
  ElTabPane,
  ElSelect,
  ElOption,
  ElIcon,
  ElRow,
  ElRate,
  ElCol,
  ElUpload,
  ElCard,
  ElContainer,
  ElHeader,
  ElMain,
  ElFooter,
  ElPopover,
  ElProgress,
  ElLoading,
  ElMessageBox,
  ElMessage,
  ElNotification,
  ElCollapseTransition,
} from "element-plus";
import "element-plus/dist/index.css";

import * as _ from "lodash-es";
import KeyReply from "./App.vue";
import { useMainStore } from "./store";

// Vue 3 compatible touch events plugin
// @ts-ignore
import Vue3TouchEvents from "vue3-touch-events";

// v-viewer plugin for image viewing/zoom functionality
// @ts-ignore
import VueViewer from "v-viewer";
import "viewerjs/dist/viewer.css";

/**
 * Device Detection Types
 *
 * Note: IE detection removed as of Vue 3.5/Vite 8 migration
 * Vite 8 targets modern browsers only (Chrome 111+, Firefox 114+, Safari 16.4+)
 */
interface DeviceInfo {
  iOS: boolean;
  iOSSDK: boolean;
  Android: boolean;
  Mobile: boolean;
  Mac: boolean;
  isMobileQuery: boolean;
}

/**
 * Speech Plugin Types
 */
interface SpeechPlugin {
  listen: (callback: (message: string, complete?: boolean) => void) => any | undefined;
  speak: (text: string) => void;
}

/**
 * Detect device information for platform-specific behavior
 *
 * @returns {DeviceInfo} Object containing device detection flags
 *
 * @property {boolean} iOS - Running on iPad or iPhone (Safari/WebView)
 * @property {boolean} iOSSDK - Running inside KeyReply iOS SDK wrapper
 * @property {boolean} Android - Running on Android device
 * @property {boolean} Mobile - Running on any mobile device (Mobi user agent)
 * @property {boolean} Mac - Running on macOS
 * @property {boolean} isMobileQuery - URL has ?mode=mobile parameter
 *
 * @techdebt User agent sniffing is unreliable; consider feature detection
 */
function detectDevice(): DeviceInfo {
  const ua = navigator.userAgent;
  const params = new URLSearchParams(location.search);
  const isMobileQuery = params.get("mode") === "mobile";
  const iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i);
  const iOSSDK = !!ua.match(/KeyReplyiOSSDK/i);
  const Android = !!ua.match(/Android/i);
  const Mobile = !!ua.match(/Mobi/i);
  const Mac = !!ua.match(/Macintosh/i);

  return {
    iOS,
    iOSSDK,
    Android,
    Mobile,
    Mac,
    isMobileQuery,
  };
}

/**
 * Create speech recognition and synthesis plugin
 *
 * @returns {SpeechPlugin} Object with listen and speak methods
 */
function createSpeechPlugin(): SpeechPlugin {
  const synth = window.speechSynthesis;
  let googleVoice: SpeechSynthesisVoice | undefined;

  if (synth) {
    synth.onvoiceschanged = function () {
      const voice = _.find(synth.getVoices(), { name: "Google US English" });
      if (voice) googleVoice = voice;
    };
  }

  return {
    listen: (callback: (message: string, complete?: boolean) => void) => {
      if (!synth) return undefined;

      const SpeechRecognitionAPI =
        window.SpeechRecognition ||
        (window as any).webkitSpeechRecognition ||
        (window as any).mozSpeechRecognition ||
        (window as any).msSpeechRecognition;

      if (!SpeechRecognitionAPI) return undefined;

      const recognition = new SpeechRecognitionAPI();
      recognition.lang = "en-US";
      recognition.interimResults = true;
      recognition.maxAlternatives = 0;

      let final_transcript = "";
      let interim_transcript = "";

      recognition.onresult = function (event: any) {
        if (typeof event.results === "undefined") {
          recognition.onend = null;
          recognition.stop();
          return;
        }

        for (let i = event.resultIndex; i < event.results.length; ++i) {
          if (event.results[i].isFinal) {
            final_transcript += event.results[i][0].transcript;
          } else {
            interim_transcript += event.results[i][0].transcript;
          }
        }

        if (interim_transcript) {
          callback(interim_transcript);
          interim_transcript = "";
        }

        if (final_transcript) {
          callback(final_transcript, true);
          final_transcript = "";
        }
      };

      return recognition;
    },

    speak: (text: string) => {
      if (!synth) return;

      text = text.replace(/&nbsp;/g, " ");
      text = text.replace(/[^\w ,.]/g, "");

      const utterThis = new SpeechSynthesisUtterance(text);

      if (googleVoice) {
        utterThis.voice = googleVoice;
        utterThis.rate = 1;
      }

      synth.speak(utterThis);
    },
  };
}

/**
 * Register Element Plus components selectively for bundle optimization
 *
 * @param {VueApp} app - Vue application instance
 */
function registerElementPlusComponents(app: VueApp): void {
  // Register components
  app.component("ElCollapseTransition", ElCollapseTransition);

  // Use component plugins
  app.use(ElCheckbox);
  app.use(ElDialog);
  app.use(ElDivider);
  app.use(ElDrawer);
  app.use(ElDropdown);
  app.use(ElDropdownMenu);
  app.use(ElDropdownItem);
  app.use(ElInput);
  app.use(ElButton);
  app.use(ElTooltip);
  app.use(ElForm);
  app.use(ElFormItem);
  app.use(ElTabs);
  app.use(ElTabPane);
  app.use(ElSelect);
  app.use(ElOption);
  app.use(ElIcon);
  app.use(ElRow);
  app.use(ElRate);
  app.use(ElCol);
  app.use(ElUpload);
  app.use(ElCard);
  app.use(ElContainer);
  app.use(ElHeader);
  app.use(ElMain);
  app.use(ElFooter);
  app.use(ElPopover);
  app.use(ElProgress);

  // Register loading directive
  app.use(ElLoading);

  // Attach Element Plus service methods to globalProperties
  app.config.globalProperties.$loading = ElLoading.service;
  app.config.globalProperties.$msgbox = ElMessageBox;
  app.config.globalProperties.$alert = ElMessageBox.alert;
  app.config.globalProperties.$confirm = ElMessageBox.confirm;
  app.config.globalProperties.$prompt = ElMessageBox.prompt;
  app.config.globalProperties.$notify = ElNotification;
  app.config.globalProperties.$message = ElMessage;
}

/**
 * Create and append the root DOM element for Vue mounting
 * The widget injects itself into the host page's body
 */
const el = document.createElement("div");
el.setAttribute("id", "app");
document.body.appendChild(el);

/**
 * Initialize Trusted Types for Content Security Policy compliance
 * Required for secure DOM manipulation in environments with strict CSP
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Trusted_Types_API
 */
setupDOMPurifyWithTrustedTypes();

/**
 * Create the Vue 3 application instance
 */
// @ts-ignore - Vue component type inference issue with .vue files
const app = createApp(KeyReply);

/**
 * Create and register Pinia store
 */
const pinia = createPinia();
app.use(pinia);

/**
 * Initialize the store and dispatch default settings
 * This must happen after Pinia is installed
 */
const store = useMainStore();

/**
 * Initialize default webchat settings via Pinia store
 *
 * This dispatch sets up the initial configuration state before any
 * external configuration is loaded from the server. These defaults
 * are overridden when FETCH_CONFIG completes.
 */
store.reload({
  title: "",

  // Launcher icon
  botIcon: null,
  logo: null,

  // Style
  background: "#FFF",
  color: "#e4392b",
  headerColor: "#e4392b",
  headerBorderColor: "#e4392b",
  agentBubbleColor: "#F24C63",
  agentTextColor: "#FFF",
  userBubbleColor: "#000",
  userTextColor: "#FFF",
  trendingTopicBanner: "#808080",
  trendingTopicTitle: "#000000",
  widgetIconTextColor: "#FFF",
  widgetIconColor: "#4e6cce",
  headerModalIconColor: "",
  userMainBackgroundColor: "",
  userMainTitleColor: "#4e6cce",

  font: "",
  position: "right",
  size: "large",

  // Behaviours
  popup: false,
  resume: false,
  suggestions: [],
  exclude: "",
  ga: "",
  showSuggestions: false,
  charLimit: {
    showCountDown: false,
    limit: 1000,
    countdownType: "increase",
  },
  showPreChatForm: { enabled: false },
  showPostChatForm: { enabled: false },
  openAutomatically: false,
  fileUpload: false,
  onlyAllowUploadWhenInLiveChat: true,
  displayLauncherOnAgentAvailability: { delay: false, number: 0 },
  disableTextInput: false,

  alwaysShowQuickReply: true,
  alwaysEnableButton: true,
  stickyMenuConfig: {
    stickyMenuLanguages: [
      {
        lang: "en",
        value: "Go to menu",
      },
    ],
  },
  videoVAConfig: {
    subtitleFontSize: 16,
    enableAlphabetButton: true,
  },
  stickyMenu: null,
  stickyMenuLanguage: "en",
  // Data
  apps: "e30=",
  message: "bWVzc2FnZQ==",
  appSource: "",
  genai_enabled: false,
  readMoreConfigure: {
    enabled: false,
    readMoreCharacterCount: 150,
  },
});

/**
 * Register Element Plus components
 */
registerElementPlusComponents(app);

/**
 * Register touch events plugin for mobile gesture support
 */
// @ts-ignore - Vue 3 touch events plugin type compatibility
app.use(Vue3TouchEvents);

/**
 * Register v-viewer plugin for image viewing
 */
app.use(VueViewer);

/**
 * Create device and speech instances
 */
const deviceInfo = detectDevice();
const speechPlugin = createSpeechPlugin();

/**
 * Register global properties (Vue 3 replacement for Vue.prototype)
 *
 * These are accessible via `this.$device`, `this.$listen`, `this.$speak` in Options API
 * For Composition API, use inject() or access via getCurrentInstance()
 */
app.config.globalProperties.$device = deviceInfo;
app.config.globalProperties.$listen = speechPlugin.listen;
app.config.globalProperties.$speak = speechPlugin.speak;

/**
 * Provide device and speech for Composition API usage via inject()
 *
 * @example
 * import { inject } from 'vue';
 * const device = inject<DeviceInfo>('device');
 * const speech = inject<SpeechPlugin>('speech');
 */
app.provide("device", deviceInfo);
app.provide("speech", speechPlugin);

/**
 * Register global utility functions (Vue 3 replacement for filters)
 *
 * Filters are removed in Vue 3. These functions are now:
 * 1. Exported from @/filters for direct import in components
 * 2. Available on globalProperties for template access
 *
 * @example Template usage:
 * <span v-html="$filters.markHTML(text, color)"></span>
 *
 * @example Script usage:
 * import { markHTML, capitalize, time } from '@/filters';
 */
app.config.globalProperties.$filters = {
  markHTML: filters.markHTML,
  capitalize: filters.capitalize,
  time: filters.time,
};

/**
 * Global KeyReply API exposed on window for external integration
 *
 * This API allows host pages to programmatically control the webchat widget.
 * Only whitelisted actions can be dispatched for security reasons.
 *
 * @namespace window.$keyreply
 * @property {string[]} actions - Whitelist of allowed action names
 * @property {Function} dispatch - Dispatch a whitelisted action to the store
 *
 * @example
 * // Open chat window from host page
 * window.$keyreply.dispatch('OPEN_CHAT_WINDOW');
 *
 * // Send a message programmatically
 * window.$keyreply.dispatch('SEND_MESSAGE', { text: 'Hello' });
 *
 * // Initialize with custom settings
 * window.$keyreply.dispatch('INITIALIZE', { server: 'https://api.example.com' });
 *
 * @security SET_WEBVIEWURL is intentionally excluded to prevent XSS attacks
 * via malicious URL injection from external scripts
 */
window.$keyreply = {
  /**
   * Whitelist of actions that can be dispatched externally
   */
  actions: [
    "START",                    // Initialize chat session
    "LOGIN",                    // Authenticate user
    "FETCH_ENDPOINT",           // Load server configuration
    "OPEN_CHAT_WINDOW",         // Show chat widget
    "CLOSE_CHAT_WINDOW",        // Hide chat widget
    "TOGGLE_CHAT_WINDOW",       // Toggle chat widget visibility
    "SEND_MESSAGE",             // Send user message
    "SEND_POSTBACK",            // Send postback/button click
    "END_LIVE_CHAT",            // End live agent session
    "CLOSE_WEBVIEW",            // Close embedded webview
    "INITIALIZE",               // Full initialization with settings
    "SET_JWT_RESEND_REQUEST",   // Retry failed JWT requests
    "SET_EMAIL_WHITELIST",      // Set allowed email domains
    "GET_LIVECHAT_STATUS",      // Check live chat availability
    "SET_NATIVE_ACTION",        // Set native app callback
    "SET_CUSTOM_SUGGESTIONS",   // Update autocomplete suggestions
    "SET_STICKY_MENU_LANGUAGE", // Change sticky menu language
    // "SET_WEBVIEWURL" // Disabled for security consideration - prevents XSS
  ],

  /**
   * Dispatch a whitelisted action to the Pinia store
   * @param {string} event - The action name to dispatch
   * @param {any} payload - The payload to pass to the action
   * @returns {Promise<any>} The result of the action, or undefined if not whitelisted
   */
  async dispatch(event: string, payload?: any) {
    if (window.$keyreply.actions.indexOf(event) > -1) {
      // Call the action on the store
      const action = store[event as keyof typeof store];
      if (typeof action === "function") {
        return (action as Function)(payload);
      }
    }
  },
};

/**
 * Mount the Vue application to the DOM
 */
app.mount("#app");

/**
 * TypeScript declarations for global properties
 */
declare module "vue" {
  interface ComponentCustomProperties {
    $device: DeviceInfo;
    $listen: SpeechPlugin["listen"];
    $speak: SpeechPlugin["speak"];
    $filters: {
      markHTML: typeof filters.markHTML;
      capitalize: typeof filters.capitalize;
      time: typeof filters.time;
    };
    $loading: typeof ElLoading.service;
    $msgbox: typeof ElMessageBox;
    $alert: typeof ElMessageBox.alert;
    $confirm: typeof ElMessageBox.confirm;
    $prompt: typeof ElMessageBox.prompt;
    $notify: typeof ElNotification;
    $message: typeof ElMessage;
  }
}

export { app, pinia, store };
